import React, { useEffect, useState } from 'react';
import { baseURL } from '../../http';
import { formatMoney } from '../../money';

const C = {
  brand:  '#F777B0',
  brand2: '#D94F92',
  ink:    '#1A1614',
  ink2:   '#3D3632',
  ink3:   '#8A8079',
  paper:  '#EDE6D5',
  paper2: '#E2D9C6',
  hair:   '#E8E0D0',
  hair2:  '#D4CABA',
  serif:  '"Instrument Serif", "Songti SC", serif',
  sans:   '"Inter", "Noto Sans SC", -apple-system, sans-serif',
  mono:   'ui-monospace, monospace',
};

const normalizeAdminToken = value => {
  const normalized = String(value || '').trim().replace(/^Bearer\s+/i, '').trim();
  // Older admin links stored values such as "<jwt>&userId=...". Only the
  // three-part JWT belongs in the Authorization header.
  const jwt = normalized.match(/[A-Za-z0-9_-]+\.[A-Za-z0-9_-]+\.[A-Za-z0-9_-]+/);
  return jwt?.[0] || normalized;
};

const adminApiUrl = (path, params = {}) => {
  const search = new URLSearchParams(params);
  const query = search.toString();
  return `${baseURL}${path}${query ? `?${query}` : ''}`;
};

const adminAuthHeaders = token => ({
  Authorization: `Bearer ${normalizeAdminToken(token)}`,
});

const ADMIN_VIEWS = new Set(['today', 'users', 'user-actions', 'finance', 'todos', 'invoices', 'messages', 'operations']);

export const getAdminLocation = () => {
  const params = new URLSearchParams(window.location.search);
  const requestedView = params.get('view');
  return {
    view: ADMIN_VIEWS.has(requestedView) ? requestedView : 'today',
    period: params.get('period') === 'today' ? 'today' : 'month',
    type: ['payment', 'withdrawal'].includes(params.get('type')) ? params.get('type') : 'all',
  };
};

const shanghaiDateKey = value => new Intl.DateTimeFormat('en-CA', {
  timeZone: 'Asia/Shanghai', year: 'numeric', month: '2-digit', day: '2-digit'
}).format(new Date(value));

const todayShanghaiDateKey = () => shanghaiDateKey(new Date());

function Kpi({ label, value, hint, warn, accent, large }) {
  return (
    <div className="admin-kpi" style={{
      border: `1px solid ${warn ? '#f5c4c4' : C.hair}`,
      borderRadius: 12, padding: '16px 20px',
      display: 'flex', flexDirection: 'column', gap: 4,
      background: warn ? '#fff8f8' : '#fff',
    }}>
      <div style={{ fontFamily: C.mono, fontSize: 10, letterSpacing: 1.4, textTransform: 'uppercase', color: warn ? '#A53636' : C.ink3, fontWeight: 600 }}>
        {label}
      </div>
      <div className="admin-kpi-value" style={{
        fontFamily: C.mono, lineHeight: 1,
        fontSize: large ? 36 : 28,
        fontWeight: 500,
        color: warn ? '#A53636' : accent ? C.brand2 : C.ink,
        fontVariantNumeric: 'tabular-nums', marginTop: 2,
      }}>
        {value}
      </div>
      {hint && <div style={{ fontSize: 11.5, color: warn ? '#c2412e' : C.ink3, marginTop: 2 }}>{hint}</div>}
    </div>
  );
}

function AdminResponsiveStyles() {
  return <style>{`
    .admin-nav-scroll::-webkit-scrollbar { display: none; }
    .admin-nav-scroll { scrollbar-width: none; }
    .admin-button-row { display: flex; gap: 8px; flex-wrap: wrap; }
    .admin-panel-shell {
      padding: 6px;
      border-radius: 24px;
      background: rgba(61,54,50,.055);
      box-shadow: inset 0 1px 0 rgba(255,255,255,.7);
      margin-bottom: 20px;
      animation: admin-rise 700ms cubic-bezier(.32,.72,0,1) both;
    }
    .admin-panel-core {
      background: rgba(255,255,255,.94);
      border-radius: 18px;
      padding: 24px;
      box-shadow: inset 0 1px 0 rgba(255,255,255,.8), 0 18px 50px -38px rgba(61,54,50,.38);
    }
    .admin-task-link {
      border: 0;
      width: 100%;
      text-align: left;
      border-radius: 16px;
      padding: 15px 16px;
      background: #f8f4eb;
      color: ${C.ink2};
      cursor: pointer;
      transition: transform 500ms cubic-bezier(.32,.72,0,1), background 500ms cubic-bezier(.32,.72,0,1);
    }
    .admin-task-link:hover { transform: translateY(-2px); background: #fff; }
    .admin-task-link:active { transform: scale(.985); }
    @keyframes admin-rise { from { opacity: 0; transform: translateY(16px); } to { opacity: 1; transform: translateY(0); } }

    @media (max-width: 767px) {
      .admin-shell {
        height: auto !important;
        min-height: 100vh;
        min-height: 100dvh;
        overflow-x: hidden;
      }
      .admin-header {
        padding: 12px 14px !important;
        margin: 8px !important;
        width: auto !important;
        flex-direction: column;
        align-items: stretch !important;
        gap: 10px;
      }
      .admin-header-main {
        width: 100%;
        flex-direction: column;
        align-items: stretch !important;
        gap: 10px !important;
        min-width: 0;
      }
      .admin-brand { justify-content: space-between; }
      .admin-nav-scroll {
        width: 100%;
        overflow-x: auto;
        overscroll-behavior-x: contain;
        -webkit-overflow-scrolling: touch;
        padding-bottom: 2px;
      }
      .admin-nav-scroll button {
        flex: 0 0 auto;
        min-height: 40px;
        padding: 8px 13px !important;
      }
      .admin-header-actions {
        justify-content: flex-end;
        min-height: 36px;
      }
      .admin-content {
        max-width: none !important;
        padding: 24px 14px 48px !important;
      }
      .admin-panel-core { padding: 18px; }
      .admin-today-actions { grid-template-columns: 1fr !important; }
      .admin-user-search { flex-direction: column; align-items: stretch !important; }
      .admin-user-search button { min-height: 44px; }
      .admin-user-detail-grid { grid-template-columns: 1fr !important; }
      .admin-todo-tabs { width: 100%; overflow-x: auto; padding-bottom: 2px; }
      .admin-api-row { grid-template-columns: 1fr !important; }
      .admin-section { margin-bottom: 28px !important; }
      .admin-kpi-grid {
        grid-template-columns: repeat(2, minmax(0, 1fr)) !important;
        gap: 10px !important;
      }
      .admin-kpi { padding: 14px !important; min-width: 0; }
      .admin-kpi-value {
        font-size: 22px !important;
        overflow-wrap: anywhere;
      }
      .admin-finance-user-head,
      .admin-invoice-head,
      .admin-message-head,
      .admin-section-head {
        flex-wrap: wrap;
        align-items: flex-start !important;
      }
      .admin-finance-user-id {
        width: 100%;
        margin-left: 0 !important;
        overflow-wrap: anywhere;
      }
      .admin-finance-summary {
        grid-template-columns: repeat(2, minmax(0, 1fr)) !important;
        padding: 14px !important;
      }
      .admin-finance-day {
        grid-template-columns: repeat(2, minmax(0, 1fr)) !important;
        gap: 7px 10px !important;
        padding: 10px 0;
        border-bottom: 1px solid ${C.hair};
      }
      .admin-finance-day:last-child { border-bottom: 0; }
      .admin-finance-day > strong { grid-column: 1 / -1; }
      .admin-finance-event {
        grid-template-columns: minmax(0, 1fr) auto !important;
        gap: 7px 10px !important;
      }
      .admin-finance-event > :nth-child(3) {
        grid-column: 1 / -1;
        grid-row: 2;
      }
      .admin-finance-event > :nth-child(4) { grid-column: 2; grid-row: 1; }
      .admin-invoice-fields { grid-template-columns: 1fr !important; }
      .admin-invoice-amount { font-size: 20px !important; }
      .admin-invoice-processing {
        flex-direction: column;
        align-items: stretch !important;
      }
      .admin-invoice-actions { justify-content: stretch !important; }
      .admin-invoice-actions button { flex: 1 1 auto; min-height: 40px; }
      .admin-message-id { width: 100%; overflow-wrap: anywhere; white-space: normal !important; }
      .admin-session-highlight {
        flex-wrap: wrap;
        gap: 10px 18px !important;
      }
      .admin-session-highlight-count { margin-left: 0 !important; }
      .admin-session-table-head { display: none !important; }
      .admin-session-row {
        grid-template-columns: repeat(2, minmax(0, 1fr)) !important;
        gap: 8px 12px;
        padding: 13px 14px !important;
      }
      .admin-session-row > :last-child {
        grid-column: 1 / -1;
        overflow-wrap: anywhere;
      }
      .admin-expired-card { flex-wrap: wrap; gap: 14px !important; }
      .admin-token-wrap { padding: 18px; box-sizing: border-box; }
      .admin-token-card {
        width: 100% !important;
        max-width: 380px;
        padding: 30px 22px !important;
        box-sizing: border-box;
      }
    }

    @media (max-width: 359px) {
      .admin-kpi-grid { grid-template-columns: 1fr !important; }
      .admin-finance-summary { grid-template-columns: 1fr !important; }
    }
  `}</style>;
}

function SectionTitle({ label, title }) {
  return (
    <div style={{ marginBottom: 14 }}>
      {label && <div style={{ fontFamily: C.mono, fontSize: 10, letterSpacing: 1.4, textTransform: 'uppercase', color: C.brand2, fontWeight: 600, marginBottom: 4 }}>{label}</div>}
      <h2 style={{ margin: 0, fontFamily: C.serif, fontSize: 26, fontWeight: 400, color: C.ink }}>{title}</h2>
    </div>
  );
}

function Btn({ children, onClick, variant = 'primary', small, disabled, active, testId }) {
  const base = {
    fontFamily: C.sans, fontWeight: 500, border: 'none',
    cursor: disabled ? 'not-allowed' : 'pointer',
    borderRadius: 999, fontSize: small ? 12 : 13,
    padding: small ? '6px 14px' : '9px 20px',
    opacity: disabled ? 0.5 : 1, transition: 'transform 500ms cubic-bezier(.32,.72,0,1), background 500ms cubic-bezier(.32,.72,0,1)',
  };
  const styles = {
    primary: { background: C.ink, color: '#fff' },
    brand:   { background: C.brand, color: '#fff', boxShadow: `0 1px 0 ${C.brand2}, 0 6px 16px -8px rgba(247,119,176,.5)` },
    ghost:   { background: active ? C.ink : 'rgba(255,255,255,.1)', color: '#fff', border: '1px solid rgba(255,255,255,.15)' },
    outline: { background: '#fff', color: C.ink2, border: `1px solid ${C.hair2}` },
  };
  return <button data-testid={testId} onClick={onClick} disabled={disabled} style={{ ...base, ...styles[variant] }}>{children}</button>;
}

function TextArea({ value, onChange, placeholder, rows = 3, testId }) {
  const [focused, setFocused] = useState(false);
  return (
    <textarea
      data-testid={testId}
      value={value}
      onChange={e => onChange(e.target.value)}
      onFocus={() => setFocused(true)}
      onBlur={() => setFocused(false)}
      placeholder={placeholder}
      rows={rows}
      style={{
        width: '100%', boxSizing: 'border-box',
        padding: '10px 12px', borderRadius: 8,
        border: `1.5px solid ${focused ? C.brand : C.hair}`,
        outline: 'none', resize: 'vertical',
        fontFamily: C.sans, fontSize: 13, lineHeight: 1.6,
        color: C.ink, background: '#fff',
      }}
    />
  );
}

function Input({ value, onChange, placeholder, type = 'text', testId, disabled }) {
  const [focused, setFocused] = useState(false);
  return (
    <input
      data-testid={testId}
      type={type}
      value={value}
      disabled={disabled}
      onChange={event => onChange(event.target.value)}
      onFocus={() => setFocused(true)}
      onBlur={() => setFocused(false)}
      placeholder={placeholder}
      style={{
        width: '100%', minHeight: 44, boxSizing: 'border-box', padding: '11px 14px',
        borderRadius: 10, border: `1.5px solid ${focused ? C.brand : C.hair}`,
        outline: 'none', fontFamily: C.sans, fontSize: 14, color: C.ink,
        background: disabled ? '#f4f0e8' : '#fff', opacity: disabled ? .7 : 1,
      }}
    />
  );
}

// ─── Token login ─────────────────────────────────────────────────────────────

const TokenPanel = () => {
  const [token, setToken] = useState('');
  const [focused, setFocused] = useState(false);
  const save = () => {
    const normalized = normalizeAdminToken(token);
    if (normalized) {
      localStorage.setItem('admintoken', normalized);
      window.location.reload();
    }
  };
  return (
    <div className="admin-token-wrap" style={{ minHeight: '100vh', background: C.paper, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
      <AdminResponsiveStyles />
      <div className="admin-token-card" style={{ background: '#fff', border: `1px solid ${C.hair}`, borderRadius: 16, padding: '48px 44px', width: 380 }}>
        <div style={{ fontFamily: C.mono, fontSize: 11, letterSpacing: 1.4, textTransform: 'uppercase', color: C.brand2, fontWeight: 600, marginBottom: 12 }}>Admin · PICSECT</div>
        <h1 style={{ margin: '0 0 32px', fontFamily: C.serif, fontSize: 36, fontWeight: 400, color: C.ink }}>登录后台</h1>
        <input
          type="password" value={token}
          onChange={e => setToken(e.target.value)}
          onFocus={() => setFocused(true)} onBlur={() => setFocused(false)}
          onKeyDown={e => e.key === 'Enter' && save()}
          placeholder="Admin Token"
          style={{
            width: '100%', boxSizing: 'border-box', padding: '13px 16px', marginBottom: 16,
            border: `1.5px solid ${focused ? C.brand : C.hair}`, borderRadius: 10, outline: 'none',
            fontFamily: C.sans, fontSize: 15, color: C.ink, background: '#fff', transition: 'border-color .2s',
          }}
        />
        <Btn onClick={save} variant="brand">进入后台</Btn>
      </div>
    </div>
  );
};

// ─── Finance page ─────────────────────────────────────────────────────────────

const financeStatusLabel = {
  paid: '已入账',
  SUCCESS: '提现成功',
  ACCEPTED: '已受理',
  FRONZEN: '处理中',
  RETURNED: '已退回',
};

const financeStatusStyle = {
  paid: { background: '#fce8f1', color: C.brand2 },
  SUCCESS: { background: '#e7f6ed', color: '#267A46' },
  ACCEPTED: { background: '#fff3d6', color: '#8A5A00' },
  FRONZEN: { background: '#e8f1ff', color: '#245A9C' },
  RETURNED: { background: '#fde8e8', color: '#A53636' },
};

const financeMoney = value => `¥${formatMoney(value, { minimumFractionDigits: 2 })}`;

export function filterFinanceUsers(users, { period = 'month', type = 'all' } = {}) {
  if (period !== 'today' && type === 'all') return users;
  const today = todayShanghaiDateKey();
  return users.map(detail => {
    const events = (detail.events || []).filter(event => {
      if (period === 'today' && shanghaiDateKey(event.occurredAt) !== today) return false;
      if (type === 'payment' && event.type !== 'selection_income') return false;
      if (type === 'withdrawal' && event.type !== 'withdrawal') return false;
      return true;
    });
    const summary = events.reduce((result, event) => {
      if (event.type === 'selection_income') result.selectionIncome += Number(event.amount || 0);
      if (event.type === 'withdrawal') {
        result.withdrawRequested += Number(event.amount || 0);
        if (event.status === 'SUCCESS') {
          result.withdrawPaid += Number(event.actualAmount || 0);
          result.withdrawalFees += Number(event.fee || 0);
        }
      }
      return result;
    }, { selectionIncome: 0, withdrawRequested: 0, withdrawPaid: 0, withdrawalFees: 0 });
    return { ...detail, events, summary, daily: period === 'today' ? [] : detail.daily };
  }).filter(detail => detail.events.length > 0);
}

export const FinancePage = ({ token, period = 'month', type = 'all', onClearFilter }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    fetch(adminApiUrl('/global/finance'), {
      headers: adminAuthHeaders(token),
    })
      .then(async r => {
        const payload = await r.json();
        if (!r.ok) throw new Error(payload.error || `HTTP ${r.status}`);
        return payload;
      })
      .then(d => { setData(d); setLoading(false); })
      .catch(requestError => { setError(requestError.message || '加载失败'); setLoading(false); });
  }, [token]);

  if (loading) return <div style={{ padding: 60, textAlign: 'center', fontFamily: C.mono, fontSize: 12, color: C.ink3 }}>loading…</div>;
  if (!data) return <div style={{ padding: 60, textAlign: 'center', color: C.ink3 }}>{error || '加载失败'}</div>;
  if (!Array.isArray(data.users)) {
    return (
      <div data-testid="finance-api-version-error" role="alert" style={{ padding: 24, background: '#fff8f8', border: '1px solid #f5c4c4', borderRadius: 12, color: '#A53636', fontSize: 13, lineHeight: 1.7 }}>
        当前服务端仍在返回旧版财务数据，缺少按用户合并的 users 明细。请确认本地服务已加载最新版 global/finance API。
      </div>
    );
  }

  const users = filterFinanceUsers(data.users, { period, type });
  const totals = users.reduce((result, item) => ({
    income: result.income + Number(item.summary?.selectionIncome || 0),
    requested: result.requested + Number(item.summary?.withdrawRequested || 0),
    paid: result.paid + Number(item.summary?.withdrawPaid || 0),
    fees: result.fees + Number(item.summary?.withdrawalFees || 0),
  }), { income: 0, requested: 0, paid: 0, fees: 0 });

  return (
    <div>
      {(period === 'today' || type !== 'all') && (
        <div data-testid="finance-active-filter" style={{ marginBottom: 18, display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12, padding: '12px 16px', borderRadius: 14, background: '#fff', color: C.ink2 }}>
          <span>{period === 'today' ? '今日' : '本月'} · {type === 'withdrawal' ? '提现明细' : type === 'payment' ? '支付明细' : '全部资金流水'}</span>
          {onClearFilter && <Btn small variant="outline" onClick={onClearFilter}>查看本月全部</Btn>}
        </div>
      )}
      <div className="admin-kpi-grid" data-testid="finance-totals" style={{ display: 'grid', gridTemplateColumns: 'repeat(4, minmax(150px, 1fr))', gap: 14, marginBottom: 22 }}>
        <Kpi label="选片收入" value={financeMoney(totals.income)} accent/>
        <Kpi label="申请提现" value={financeMoney(totals.requested)}/>
        <Kpi label="实际到账" value={financeMoney(totals.paid)}/>
        <Kpi label="提现手续费（差额）" value={financeMoney(totals.fees)}/>
      </div>

      <div data-testid="finance-channel-cost-notice" style={{ marginBottom: 22, padding: '12px 16px', background: C.paper2, border: `1px solid ${C.hair}`, borderRadius: 10, fontSize: 12.5, lineHeight: 1.6, color: C.ink3 }}>
        提现手续费按“申请金额 − 实际到账”展示。微信支付通道成本尚未入库，因此这里不计算或展示平台净收益。
      </div>

      {users.length === 0 && (
        <div style={{ padding: 48, textAlign: 'center', background: '#fff', border: `1px solid ${C.hair}`, borderRadius: 12, color: C.ink3 }}>
          {period === 'today' || type !== 'all' ? '当前筛选下暂无资金流水' : '暂无用户财务数据'}
        </div>
      )}

      <div style={{ display: 'grid', gap: 18 }}>
        {users.map(detail => (
          <div key={detail.user.id} data-testid={`finance-user-${detail.user.id}`} style={{ background: '#fff', border: `1px solid ${C.hair}`, borderRadius: 14, overflow: 'hidden' }}>
            <div className="admin-finance-user-head" style={{ padding: '18px 20px', borderBottom: `1px solid ${C.hair}`, display: 'flex', alignItems: 'center', gap: 18, flexWrap: 'wrap' }}>
              <div>
                <div style={{ fontFamily: C.serif, fontSize: 23, color: C.ink }}>{detail.user.username || '未命名用户'}</div>
                <div style={{ marginTop: 3, fontFamily: C.mono, fontSize: 11, color: C.ink3 }}>{detail.user.phone || detail.user.id}</div>
              </div>
              <div className="admin-finance-user-id" style={{ marginLeft: 'auto', fontFamily: C.mono, fontSize: 10, color: C.ink3 }}>
                USER · {detail.user.id}
              </div>
            </div>

            <div className="admin-finance-summary" data-testid={`finance-user-summary-${detail.user.id}`} style={{ padding: '14px 20px', display: 'grid', gridTemplateColumns: 'repeat(6, minmax(110px, 1fr))', gap: 12, borderBottom: `1px solid ${C.hair}` }}>
              {[
                ['选片收入', detail.summary.selectionIncome],
                ['申请提现', detail.summary.withdrawRequested],
                ['实际到账', detail.summary.withdrawPaid],
                ['提现手续费', detail.summary.withdrawalFees],
                ['可提现余额', detail.user.balance],
                ['结算中余额', detail.user.frozenBalance],
              ].map(([label, value]) => (
                <div key={label} style={{ minWidth: 0 }}>
                  <div style={{ marginBottom: 4, fontSize: 10, color: C.ink3 }}>{label}</div>
                  <strong style={{ fontFamily: C.mono, fontSize: 13, color: label === '选片收入' ? C.brand2 : C.ink, fontVariantNumeric: 'tabular-nums' }}>{financeMoney(value)}</strong>
                </div>
              ))}
            </div>

            <div style={{ padding: '16px 20px', borderBottom: `1px solid ${C.hair}`, background: '#fffdf8' }}>
              <div style={{ fontFamily: C.mono, fontSize: 10, letterSpacing: 1.2, color: C.brand2, marginBottom: 10 }}>按北京时间每日汇总</div>
              <div style={{ display: 'grid', gap: 8 }}>
                {detail.daily.map(day => (
                  <div className="admin-finance-day" key={day.date} data-testid={`finance-day-${detail.user.id}-${day.date}`} style={{ display: 'grid', gridTemplateColumns: '110px repeat(4, minmax(110px, 1fr))', gap: 12, alignItems: 'center', fontFamily: C.mono, fontSize: 12 }}>
                    <strong>{day.date}</strong>
                    <span>收入 {financeMoney(day.selectionIncome)}</span>
                    <span>提现 {financeMoney(day.withdrawRequested)}</span>
                    <span>到账 {financeMoney(day.withdrawPaid)}</span>
                    <span>手续费 {financeMoney(day.withdrawalFees)}</span>
                  </div>
                ))}
              </div>
            </div>

            <div style={{ padding: '4px 20px 8px' }}>
              {detail.events.map((event, index) => {
                const withdrawal = event.type === 'withdrawal';
                const successful = event.status === 'SUCCESS';
                const returned = event.status === 'RETURNED';
                const eventStatusStyle = financeStatusStyle[event.status] || { background: C.paper2, color: C.ink3 };
                return (
                  <div className="admin-finance-event" key={`${event.type}-${event.id}`} data-testid={`finance-event-${event.id}`} style={{ display: 'grid', gridTemplateColumns: '150px 90px 1fr auto', gap: 14, alignItems: 'center', padding: '12px 0', borderBottom: index < detail.events.length - 1 ? `1px solid ${C.hair}` : 'none' }}>
                    <span style={{ fontFamily: C.mono, fontSize: 11, color: C.ink3 }}>{new Date(event.occurredAt).toLocaleString('zh-CN', { timeZone: 'Asia/Shanghai', hour12: false })}</span>
                    <strong style={{ fontSize: 13, color: withdrawal ? C.ink2 : C.brand2 }}>{event.label}</strong>
                    <span style={{ fontSize: 12, color: C.ink3 }}>
                      {withdrawal
                        ? successful
                          ? `申请 ${financeMoney(event.amount)} → 实际到账 ${financeMoney(event.actualAmount)} · 手续费 ${financeMoney(event.fee)}`
                          : returned
                            ? `申请 ${financeMoney(event.amount)} · 款项已退回`
                            : `申请 ${financeMoney(event.amount)} · 尚未到账`
                        : `入账 ${financeMoney(event.amount)}`}
                      {event.failureReason ? ` · ${event.failureReason}` : ''}
                    </span>
                    <span style={{ padding: '3px 8px', borderRadius: 10, background: eventStatusStyle.background, color: eventStatusStyle.color, fontSize: 10.5, fontWeight: 700, whiteSpace: 'nowrap' }}>{financeStatusLabel[event.status] || event.status}</span>
                  </div>
                );
              })}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

// ─── Invoices page ──────────────────────────────────────────────────────────

const invoiceStatusMeta = {
  pending: { label: '待处理', background: '#fff3d6', color: '#8A5A00' },
  processing: { label: '处理中', background: '#e8f1ff', color: '#245A9C' },
  completed: { label: '已完成', background: '#e7f6ed', color: '#267A46' },
  rejected: { label: '已拒绝', background: '#fde8e8', color: '#A53636' },
};

function InvoiceCard({ invoice, token, onChanged }) {
  const [invoiceNumber, setInvoiceNumber] = useState(invoice.invoiceNumber || '');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const status = invoiceStatusMeta[invoice.status] || { label: invoice.status, background: C.paper2, color: C.ink3 };
  const user = invoice.user || invoice.User || {};

  const updateStatus = async (action, body = {}) => {
    setSaving(true);
    setError('');
    try {
      const response = await fetch(`${baseURL}/global/invoice/${invoice.id}/${action}`, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${normalizeAdminToken(token)}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(body),
      });
      const payload = await response.json().catch(() => ({}));
      if (!response.ok) throw new Error(payload.error || `HTTP ${response.status}`);
      await onChanged();
    } catch (requestError) {
      setError(requestError.message || '操作失败');
    } finally {
      setSaving(false);
    }
  };

  const reject = () => {
    if (window.confirm('确认拒绝这条开票申请？拒绝后用户可重新申请。')) {
      updateStatus('reject');
    }
  };

  const complete = () => {
    const normalized = invoiceNumber.trim();
    if (!normalized) {
      setError('请填写发票号码');
      return;
    }
    updateStatus('approve', { invoiceNumber: normalized });
  };

  const Field = ({ label, children, mono }) => (
    <div style={{ minWidth: 0 }}>
      <div style={{ fontFamily: C.mono, fontSize: 10, letterSpacing: 1.1, textTransform: 'uppercase', color: C.ink3, marginBottom: 5 }}>{label}</div>
      <div style={{ fontFamily: mono ? C.mono : C.sans, fontSize: 13, lineHeight: 1.6, color: C.ink2, overflowWrap: 'anywhere' }}>{children || '—'}</div>
    </div>
  );

  return (
    <article data-testid={`admin-invoice-${invoice.id}`} style={{ background: '#fff', border: `1px solid ${C.hair}`, borderRadius: 12, overflow: 'hidden' }}>
      <div className="admin-invoice-head" style={{ padding: '16px 20px', borderBottom: `1px solid ${C.hair}`, display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 20 }}>
        <div style={{ minWidth: 0 }}>
          <div style={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 10, marginBottom: 6 }}>
            <span style={{ fontFamily: C.serif, fontSize: 23, lineHeight: 1.1, color: C.ink }}>{invoice.title}</span>
            <span data-testid={`admin-invoice-status-${invoice.id}`} data-status={invoice.status} style={{ padding: '3px 9px', borderRadius: 12, background: status.background, color: status.color, fontSize: 11, fontWeight: 700 }}>
              {status.label}
            </span>
          </div>
          <div style={{ fontFamily: C.mono, fontSize: 11, color: C.ink3 }}>
            {invoice.id} · {invoice.requestDate ? new Date(invoice.requestDate).toLocaleString() : '未知申请时间'}
          </div>
        </div>
        <div className="admin-invoice-amount" style={{ fontFamily: C.mono, fontSize: 24, color: C.ink, fontVariantNumeric: 'tabular-nums', whiteSpace: 'nowrap' }}>
          ¥{Number(invoice.amount || 0).toFixed(2)}
        </div>
      </div>

      <div className="admin-invoice-fields" style={{ padding: '18px 20px', display: 'grid', gridTemplateColumns: 'repeat(3, minmax(0, 1fr))', gap: '18px 28px' }}>
        <Field label="发票类型">{invoice.type === 'company' ? '企业' : '个人'}</Field>
        <Field label="税号" mono>{invoice.taxNumber}</Field>
        <Field label="接收邮箱" mono>{invoice.email}</Field>
        <Field label="申请用户">{user.username || `用户 ${user.id || invoice.userId || '—'}`}</Field>
        <Field label="手机号" mono>{user.phone}</Field>
        <Field label="发票号码" mono>{invoice.invoiceNumber}</Field>
        <div style={{ gridColumn: '1 / -1' }}><Field label="用户备注">{invoice.remark}</Field></div>
      </div>

      {['pending', 'processing'].includes(invoice.status) && (
        <div style={{ padding: '14px 20px', borderTop: `1px solid ${C.hair}`, background: '#fcfaf6' }}>
          {invoice.status === 'processing' && (
            <div className="admin-invoice-processing" style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 10 }}>
              <input
                data-testid={`admin-invoice-number-${invoice.id}`}
                value={invoiceNumber}
                onChange={event => setInvoiceNumber(event.target.value)}
                placeholder="填写开具后的发票号码"
                style={{ flex: 1, minWidth: 0, padding: '10px 12px', border: `1.5px solid ${C.hair2}`, borderRadius: 8, outline: 'none', fontFamily: C.mono, fontSize: 13, color: C.ink, background: '#fff' }}
              />
              <Btn testId={`admin-invoice-complete-${invoice.id}`} small variant="brand" onClick={complete} disabled={saving}>
                完成开票
              </Btn>
            </div>
          )}
          <div className="admin-invoice-actions admin-button-row" style={{ display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
            {invoice.status === 'pending' && (
              <Btn testId={`admin-invoice-processing-${invoice.id}`} small variant="primary" onClick={() => updateStatus('processing')} disabled={saving}>
                开始处理
              </Btn>
            )}
            <Btn testId={`admin-invoice-reject-${invoice.id}`} small variant="outline" onClick={reject} disabled={saving}>
              拒绝申请
            </Btn>
          </div>
          {error && <div role="alert" style={{ marginTop: 9, textAlign: 'right', fontSize: 12, color: '#A53636' }}>{error}</div>}
        </div>
      )}
    </article>
  );
}

export const InvoicesPage = ({ token, onDashboardChanged }) => {
  const [invoices, setInvoices] = useState([]);
  const [status, setStatus] = useState('');
  const [page, setPage] = useState(1);
  const [pagination, setPagination] = useState({ total: 0, totalPages: 0 });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const fetchInvoices = async () => {
    setLoading(true);
    setError('');
    try {
      const params = new URLSearchParams({ page: String(page), limit: '20' });
      if (status) params.set('status', status);
      const response = await fetch(`${baseURL}/global/invoices?${params.toString()}`, {
        headers: { Authorization: `Bearer ${normalizeAdminToken(token)}` },
      });
      const payload = await response.json().catch(() => ({}));
      if (!response.ok) throw new Error(payload.error || `HTTP ${response.status}`);
      setInvoices(Array.isArray(payload.invoices) ? payload.invoices : []);
      setPagination({ total: Number(payload.total || 0), totalPages: Number(payload.totalPages || 0) });
    } catch (requestError) {
      setError(requestError.message || '加载失败');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchInvoices(); }, [token, status, page]);

  const refreshAfterAction = async () => {
    await fetchInvoices();
    onDashboardChanged();
  };

  const changeStatus = nextStatus => {
    setStatus(nextStatus);
    setPage(1);
  };

  return (
    <div data-testid="admin-invoices-page">
      <div className="admin-section-head" style={{ marginBottom: 22, display: 'flex', alignItems: 'flex-end', justifyContent: 'space-between', gap: 20 }}>
        <SectionTitle label="Invoices · 本月" title="本月发票管理"/>
        <div style={{ fontFamily: C.mono, fontSize: 11, color: C.ink3 }}>共 {pagination.total} 条</div>
      </div>
      <div style={{ display: 'flex', gap: 7, flexWrap: 'wrap', marginBottom: 18 }}>
        {[
          ['', '全部'], ['pending', '待处理'], ['processing', '处理中'], ['completed', '已完成'], ['rejected', '已拒绝'],
        ].map(([value, label]) => (
          <Btn key={value || 'all'} testId={`admin-invoice-filter-${value || 'all'}`} small variant={status === value ? 'primary' : 'outline'} onClick={() => changeStatus(value)} active={status === value}>
            {label}
          </Btn>
        ))}
      </div>

      {loading ? (
        <div style={{ padding: 60, textAlign: 'center', fontFamily: C.mono, fontSize: 12, color: C.ink3 }}>loading…</div>
      ) : error ? (
        <div role="alert" style={{ padding: 24, background: '#fff8f8', border: '1px solid #f5c4c4', borderRadius: 12, color: '#A53636' }}>{error}</div>
      ) : invoices.length === 0 ? (
        <div style={{ padding: 60, textAlign: 'center', background: '#fff', border: `1px solid ${C.hair}`, borderRadius: 12, color: C.ink3 }}>当前筛选条件下没有发票申请</div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
          {invoices.map(invoice => <InvoiceCard key={invoice.id} invoice={invoice} token={token} onChanged={refreshAfterAction}/>) }
        </div>
      )}

      {pagination.totalPages > 1 && (
        <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', gap: 12, marginTop: 20 }}>
          <Btn small variant="outline" onClick={() => setPage(current => Math.max(1, current - 1))} disabled={page <= 1}>上一页</Btn>
          <span style={{ fontFamily: C.mono, fontSize: 12, color: C.ink3 }}>{page} / {pagination.totalPages}</span>
          <Btn small variant="outline" onClick={() => setPage(current => Math.min(pagination.totalPages, current + 1))} disabled={page >= pagination.totalPages}>下一页</Btn>
        </div>
      )}
    </div>
  );
};

// ─── Messages page ───────────────────────────────────────────────────────────

function getMessageReply(message) {
  if (message.reply) return message.reply;
  if (Array.isArray(message.replies) && message.replies.length > 0) {
    return message.replies[message.replies.length - 1]?.content;
  }
  return '';
}

function MessageCard({ message, onReply }) {
  const [reply, setReply] = useState(getMessageReply(message) || '');
  const [saving, setSaving] = useState(false);
  const user = message.user || message.User || {};
  const existingReply = getMessageReply(message);
  const userName = user.username || message.username || `用户 ${message.userId || message.id}`;
  const phone = user.phone || message.phone;

  const submit = () => {
    const text = reply.trim();
    if (!text) return;
    setSaving(true);
    onReply(message.id, text).finally(() => setSaving(false));
  };

  return (
    <div data-testid={`admin-message-${message.id}`} style={{ background: '#fff', border: `1px solid ${C.hair}`, borderRadius: 12, overflow: 'hidden' }}>
      <div className="admin-message-head" style={{ padding: '16px 20px', borderBottom: `1px solid ${C.hair}`, display: 'flex', justifyContent: 'space-between', gap: 18 }}>
        <div style={{ minWidth: 0 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 7 }}>
            <span style={{ fontFamily: C.serif, fontSize: 22, color: C.ink, lineHeight: 1 }}>{userName}</span>
            {!existingReply && (
              <span style={{ padding: '3px 8px', borderRadius: 10, background: '#fff8f8', color: '#A53636', fontSize: 11, fontWeight: 700 }}>
                待回复
              </span>
            )}
          </div>
          <div style={{ fontFamily: C.mono, fontSize: 11, color: C.ink3 }}>
            {phone || '无手机号'} · {message.createdAt ? new Date(message.createdAt).toLocaleString() : '未知时间'}
          </div>
        </div>
        {message.id && (
          <div className="admin-message-id" style={{ fontFamily: C.mono, fontSize: 11, color: C.ink3, whiteSpace: 'nowrap' }}>
            #{message.id}
          </div>
        )}
      </div>

      <div style={{ padding: '18px 20px 20px' }}>
        <div style={{ fontFamily: C.mono, fontSize: 10, letterSpacing: 1.3, textTransform: 'uppercase', color: C.ink3, marginBottom: 7 }}>
          用户留言
        </div>
        <div style={{ fontSize: 14, lineHeight: 1.7, color: C.ink2, whiteSpace: 'pre-wrap', marginBottom: 18 }}>
          {message.content}
        </div>

        <div style={{ fontFamily: C.mono, fontSize: 10, letterSpacing: 1.3, textTransform: 'uppercase', color: C.brand2, marginBottom: 7 }}>
          客服回复
        </div>
        <TextArea testId={`admin-message-reply-input-${message.id}`} value={reply} onChange={setReply} placeholder="输入回复内容..."/>
        <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: 10 }}>
          <Btn testId={`admin-message-reply-submit-${message.id}`} small variant="brand" onClick={submit} disabled={saving || !reply.trim()}>
            {saving ? '保存中...' : existingReply ? '更新回复' : '发送回复'}
          </Btn>
        </div>
      </div>
    </div>
  );
}

const MessagesPage = ({ token, period = 'month' }) => {
  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const fetchMessages = () => {
    setLoading(true);
    setError('');
    fetch(adminApiUrl('/global/messages'), { headers: adminAuthHeaders(token) })
      .then(r => {
        if (!r.ok) throw new Error(`HTTP ${r.status}`);
        return r.json();
      })
      .then(d => {
        const list = Array.isArray(d) ? d : (d.messages || d.data || []);
        setMessages(list);
      })
      .catch(err => setError(err.message || '加载失败'))
      .finally(() => setLoading(false));
  };

  useEffect(() => { fetchMessages(); }, [token]);

  const handleReply = (messageId, reply) => {
    return fetch(adminApiUrl(`/global/message/${messageId}/reply`), {
      method: 'POST',
      headers: { ...adminAuthHeaders(token), 'Content-Type': 'application/json' },
      body: JSON.stringify({ content: reply }),
    })
      .then(r => {
        if (!r.ok) throw new Error(`HTTP ${r.status}`);
        return r.json().catch(() => ({}));
      })
      .then(() => fetchMessages());
  };

  if (loading) return <div style={{ padding: 60, textAlign: 'center', fontFamily: C.mono, fontSize: 12, color: C.ink3 }}>loading...</div>;
  if (error) return (
    <div style={{ background: '#fff8f8', border: '1px solid #f5c4c4', borderRadius: 12, padding: '20px 24px', color: '#A53636', fontSize: 13 }}>
      留言加载失败：{error}
    </div>
  );

  const visibleMessages = period === 'today'
    ? messages.filter(message => message.createdAt && shanghaiDateKey(message.createdAt) === todayShanghaiDateKey())
    : messages;

  return (
    <div>
      <div style={{ marginBottom: 28, display: 'flex', justifyContent: 'space-between', alignItems: 'flex-end', gap: 16 }}>
        <SectionTitle label="Support" title="用户留言"/>
        <Btn small variant="outline" onClick={fetchMessages}>刷新</Btn>
      </div>

      {visibleMessages.length === 0 ? (
        <div style={{ padding: '80px 0', textAlign: 'center', border: `1px dashed ${C.hair2}`, borderRadius: 12, color: C.ink3, background: '#fff' }}>
          暂无留言
        </div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
          {visibleMessages.map(message => (
            <MessageCard key={message.id} message={message} onReply={handleReply}/>
          ))}
        </div>
      )}
    </div>
  );
};

function TodayOverview({ today, summary, income, finance, pending, onNavigate }) {
  const actions = [
    { label: '待处理提现', value: pending.withdrawCount ?? finance.pendingWithdrawCount ?? 0, view: 'finance', params: { type: 'withdrawal' } },
    { label: '待审发票', value: pending.invoiceCount ?? 0, view: 'invoices' },
    { label: '未读回复', value: pending.unreadReplyCount ?? 0, view: 'messages', params: { period: 'today' } },
    { label: '系统任务', value: '查看运行状态', view: 'operations', params: { period: 'today' } },
  ];
  return (
    <div>
      <div style={{ marginBottom: 34 }}>
        <div style={{ fontFamily: C.mono, fontSize: 10, letterSpacing: 1.8, color: C.brand2, marginBottom: 8 }}>TODAY · 今日</div>
        <h1 style={{ margin: 0, fontFamily: C.serif, fontSize: 'clamp(38px, 6vw, 68px)', fontWeight: 400, lineHeight: .98, color: C.ink }}>今天发生了什么</h1>
        <p style={{ maxWidth: 580, margin: '16px 0 0', color: C.ink3, lineHeight: 1.7 }}>只保留今天的重要变化和需要处理的事情。具体用户、金额与状态进入对应明细查看。</p>
      </div>

      <div className="admin-panel-shell">
        <div className="admin-panel-core">
          <SectionTitle label="Today at a glance" title="今日业务"/>
          <div className="admin-kpi-grid" style={{ display: 'grid', gridTemplateColumns: 'repeat(4, minmax(0, 1fr))', gap: 12 }}>
            <Kpi label="新增用户" value={today.todayNewUserCount ?? summary.todayNewUserCount ?? 0}/>
            <Kpi label="活跃用户" value={summary.todayActiveUserCount ?? 0}/>
            <Kpi label="新增订单" value={today.todayOrderCount ?? 0}/>
            <Kpi label="选片提交" value={today.todaySelectionCount ?? 0}/>
          </div>
        </div>
      </div>

      <div className="admin-panel-shell">
        <div className="admin-panel-core">
          <SectionTitle label="Money movement" title="今日资金"/>
          <div className="admin-kpi-grid" style={{ display: 'grid', gridTemplateColumns: 'repeat(4, minmax(0, 1fr))', gap: 12 }}>
            <Kpi label="选片支付" value={financeMoney(today.todayPaidSelectionAmount)} accent hint={`${today.todayPaidSelectionCount ?? 0} 笔`}/>
            <Kpi label="新订阅" value={financeMoney(today.todaySubscriptionAmount)} hint={`${today.todaySubscriptionCount ?? 0} 笔`}/>
            <Kpi label="提现申请" value={financeMoney(today.todayWithdrawalRequestedAmount)} hint={`${today.todayWithdrawalRequestedCount ?? 0} 笔`}/>
            <Kpi label="提现到账" value={financeMoney(today.todayWithdrawalSuccessfulAmount)} hint={`${today.todayWithdrawalSuccessfulCount ?? 0} 笔`}/>
          </div>
        </div>
      </div>

      <div className="admin-panel-shell">
        <div className="admin-panel-core">
          <SectionTitle label="Service pulse" title="今日服务状态"/>
          <div className="admin-kpi-grid" style={{ display: 'grid', gridTemplateColumns: 'repeat(4, minmax(0, 1fr))', gap: 12 }}>
            <Kpi label="提现处理中 / 退回" value={`${today.todayWithdrawalProcessingCount ?? 0} / ${today.todayWithdrawalReturnedCount ?? 0}`} warn={(today.todayWithdrawalReturnedCount ?? 0) > 0}/>
            <Kpi label="用户留言" value={today.todayMessageCount ?? 0}/>
            <Kpi label="短信成功" value={today.todaySmsAcceptedCount ?? 0}/>
            <Kpi label="短信失败" value={today.todaySmsFailedCount ?? 0} warn={(today.todaySmsFailedCount ?? 0) > 0}/>
          </div>
        </div>
      </div>

      <div className="admin-panel-shell">
        <div className="admin-panel-core">
          <SectionTitle label="Action center" title="现在要处理什么"/>
          <div className="admin-today-actions" style={{ display: 'grid', gridTemplateColumns: 'repeat(2, minmax(0, 1fr))', gap: 10 }}>
            {actions.map(action => (
              <button key={action.label} className="admin-task-link" onClick={() => onNavigate(action.view, action.params)}>
                <span style={{ display: 'block', fontSize: 11, letterSpacing: 1.1, color: C.ink3, marginBottom: 6 }}>{action.label}</span>
                <strong style={{ fontFamily: C.serif, fontSize: 25, fontWeight: 400 }}>{action.value}</strong>
              </button>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}

export function OperationsPage({ token }) {
  const [data, setData] = useState(null);
  const [error, setError] = useState('');
  useEffect(() => {
    fetch(adminApiUrl('/global/operations'), { headers: adminAuthHeaders(token) })
      .then(async response => {
        const payload = await response.json();
        if (!response.ok) throw new Error(payload.error || `HTTP ${response.status}`);
        setData(payload);
      })
      .catch(requestError => setError(requestError.message || '加载失败'));
  }, [token]);

  if (error) return <div role="alert" style={{ padding: 22, borderRadius: 16, background: '#fff8f8', color: '#A53636' }}>系统任务加载失败：{error}</div>;
  if (!data) return <div style={{ padding: 60, textAlign: 'center', color: C.ink3 }}>loading…</div>;
  const tasks = Object.values(data.tasks || {});
  return (
    <div>
      <div style={{ marginBottom: 30 }}>
        <SectionTitle label={`Operations · ${data.environment}`} title="系统任务运行明细"/>
        <div style={{ color: C.ink3, fontSize: 13 }}>今天的定时任务运行结果与订单清理记录。</div>
      </div>
      <div className="admin-panel-shell">
        <div className="admin-panel-core">
          <div className="admin-kpi-grid" style={{ display: 'grid', gridTemplateColumns: 'repeat(2, minmax(0, 1fr))', gap: 12 }}>
            {tasks.map(task => (
              <div key={task.label} style={{ padding: 18, borderRadius: 16, background: '#f8f4eb' }}>
                <div style={{ fontSize: 11, color: C.ink3 }}>{task.schedule} · {task.enabled ? '已启用' : '未启用'}</div>
                <div style={{ marginTop: 7, fontFamily: C.serif, fontSize: 22 }}>{task.label}</div>
                <div style={{ marginTop: 12, fontFamily: C.mono, fontSize: 12, color: task.failures ? '#A53636' : C.ink2 }}>
                  运行 {task.runs} 次 · 处理 {task.affected ?? task.succeeded ?? 0} · 异常 {(task.failures || 0) + (task.itemFailures || 0)}
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
      <div className="admin-panel-shell">
        <div className="admin-panel-core">
          <SectionTitle label="Order cleanup" title="订单清理明细"/>
          {(data.orderChanges || []).length === 0 ? (
            <div style={{ color: C.ink3 }}>今日没有订单被移入回收站或释放资源。</div>
          ) : (data.orderChanges || []).map(order => (
            <div key={`${order.action}-${order.id}`} style={{ padding: '12px 0', borderBottom: `1px solid ${C.hair}`, display: 'flex', justifyContent: 'space-between', gap: 16 }}>
              <div><strong>{order.albumTitle || order.customerName || '未命名订单'}</strong><div style={{ marginTop: 3, fontFamily: C.mono, fontSize: 11, color: C.ink3 }}>{order.id}</div></div>
              <span style={{ color: order.action === 'trash' ? '#8A5A00' : '#267A46', whiteSpace: 'nowrap' }}>{order.action === 'trash' ? '移入回收站' : '释放资源'}</span>
            </div>
          ))}
          {(data.omittedOrderChangeCount || 0) > 0 && <div style={{ marginTop: 12, color: C.ink3 }}>另有 {data.omittedOrderChangeCount} 条记录未在卡片中保留。</div>}
        </div>
      </div>
    </div>
  );
}

// ─── Users & guarded operations ─────────────────────────────────────────────

const formatDateTime = value => value ? new Date(value).toLocaleString('zh-CN', { hour12: false }) : '—';

function UserDetailSection({ title, items, empty, renderItem }) {
  return (
    <div style={{ padding: 18, background: '#fff', border: `1px solid ${C.hair}`, borderRadius: 14, minWidth: 0 }}>
      <div style={{ fontFamily: C.serif, fontSize: 22, marginBottom: 13 }}>{title}</div>
      {!items?.length ? <div style={{ color: C.ink3, fontSize: 13 }}>{empty}</div> : items.map(renderItem)}
    </div>
  );
}

export function UsersPage({ token, onOpenActions }) {
  const [query, setQuery] = useState('');
  const [status, setStatus] = useState('all');
  const [results, setResults] = useState([]);
  const [selected, setSelected] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const loadDetail = async user => {
    setLoading(true);
    setError('');
    try {
      const response = await fetch(adminApiUrl(`/global/users/${user.id}`), { headers: adminAuthHeaders(token) });
      const payload = await response.json().catch(() => ({}));
      if (!response.ok) throw new Error(payload.error || `HTTP ${response.status}`);
      setSelected(payload);
    } catch (requestError) {
      setError(requestError.message || '用户详情加载失败');
    } finally {
      setLoading(false);
    }
  };

  const search = async event => {
    event?.preventDefault();
    const keyword = query.trim();
    if (!keyword) return;
    setLoading(true);
    setError('');
    setSelected(null);
    try {
      const response = await fetch(adminApiUrl('/global/users', { search: keyword, status, page: 1, pageSize: 20 }), { headers: adminAuthHeaders(token) });
      const payload = await response.json().catch(() => ({}));
      if (!response.ok) throw new Error(payload.error || `HTTP ${response.status}`);
      if (!Array.isArray(payload.users)) throw new Error('用户搜索响应缺少 users 列表');
      setResults(payload.users);
    } catch (requestError) {
      setResults([]);
      setError(requestError.message || '用户搜索失败');
    } finally {
      setLoading(false);
    }
  };

  const detail = selected || {};
  const user = detail.user || {};
  const recent = detail.recent || {};
  const summary = detail.summary || {};
  return (
    <div>
      <div style={{ marginBottom: 28 }}>
        <SectionTitle label="Users" title="查找用户"/>
        <p style={{ margin: 0, color: C.ink3, fontSize: 13 }}>用手机号、用户名或完整用户 ID 定位账户，再查看订阅与资金链路。</p>
      </div>
      <form className="admin-user-search" onSubmit={search} style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 16 }}>
        <Input testId="admin-user-search-input" value={query} onChange={setQuery} placeholder="手机号 / 用户名 / 用户 ID"/>
        <select data-testid="admin-user-status" value={status} onChange={event => setStatus(event.target.value)} style={{ minHeight: 44, padding: '0 34px 0 13px', border: `1.5px solid ${C.hair}`, borderRadius: 10, background: '#fff', color: C.ink2, fontFamily: C.sans, fontSize: 13 }}>
          <option value="all">全部状态</option>
          <option value="active">会员有效</option>
          <option value="expired">会员过期</option>
          <option value="banned">已封禁</option>
        </select>
        <Btn testId="admin-user-search-submit" variant="brand" disabled={loading || !query.trim()}>{loading ? '查询中…' : '搜索'}</Btn>
      </form>
      {error && (
        <div data-testid="admin-user-request-error" role="alert" style={{ marginBottom: 18, padding: '14px 16px', border: '1px solid #f5c4c4', borderRadius: 12, background: '#fff8f8', color: '#A53636', fontSize: 13 }}>
          用户数据加载失败：{error}
        </div>
      )}
      {!selected && results.length > 0 && (
        <div style={{ display: 'grid', gap: 10 }}>
          {results.map(result => (
            <button key={result.id} data-testid={`admin-user-result-${result.id}`} onClick={() => loadDetail(result)} className="admin-task-link">
              <span style={{ display: 'block', fontFamily: C.serif, fontSize: 22 }}>{result.username || '未命名用户'}</span>
              <span style={{ display: 'block', marginTop: 5, fontFamily: C.mono, fontSize: 11, color: C.ink3 }}>{result.phone || '无手机号'} · {result.id}</span>
            </button>
          ))}
        </div>
      )}
      {selected && (
        <div data-testid="admin-user-detail">
          <div className="admin-panel-shell">
            <div className="admin-panel-core">
              <div className="admin-section-head" style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 16 }}>
                <div>
                  <div style={{ fontFamily: C.serif, fontSize: 30 }}>{user.username || '未命名用户'}</div>
                  <div style={{ marginTop: 5, fontFamily: C.mono, color: C.ink3, fontSize: 11 }}>{user.phone || '无手机号'} · {user.id}</div>
                  <div style={{ marginTop: 8, color: user.bannedAt ? '#A53636' : C.ink2, fontSize: 13 }}>等级 {user.level ?? '—'} · 会员到期 {formatDateTime(user.subscriptionEndDate)}{user.bannedAt ? ` · 已封禁：${user.banReason || '未填写原因'}` : ''}</div>
                </div>
                <Btn variant="outline" onClick={() => onOpenActions(user.id)}>用户操作</Btn>
              </div>
            </div>
          </div>
          <div className="admin-kpi-grid" style={{ display: 'grid', gridTemplateColumns: 'repeat(4, minmax(0, 1fr))', gap: 10, marginBottom: 12 }}>
            <Kpi label="订单" value={summary.orderCount ?? 0} hint={`相册 ${summary.albumCount ?? 0}`}/>
            <Kpi label="照片" value={summary.pictureCount ?? 0}/>
            <Kpi label="付费选片" value={summary.paidSelectionCount ?? 0} hint={financeMoney(summary.paidSelectionAmount)}/>
            <Kpi label="成功提现" value={summary.withdrawalCount ?? 0} hint={financeMoney(summary.successfulWithdrawalAmount)}/>
          </div>
          <div className="admin-user-detail-grid" style={{ display: 'grid', gridTemplateColumns: 'repeat(2, minmax(0, 1fr))', gap: 12 }}>
            <UserDetailSection title="订阅" items={recent.bookings} empty="暂无订阅记录" renderItem={item => <div key={item.id} style={{ padding: '9px 0', borderTop: `1px solid ${C.hair}`, fontSize: 13 }}>{item.subject || item.name || '会员订阅'} · {financeMoney(item.amount)} · {formatDateTime(item.endDate || item.createdAt)}</div>}/>
            <UserDetailSection title="订单" items={[]} empty={`共有 ${summary.orderCount ?? 0} 个订单；当前详情接口仅返回数量，订单明细接口待补。`} renderItem={() => null}/>
            <UserDetailSection title="支付" items={recent.paidSelections} empty="暂无付费选片记录" renderItem={item => <div key={item.id} style={{ padding: '9px 0', borderTop: `1px solid ${C.hair}`, fontSize: 13 }}>{financeMoney(item.totalPrice || item.amount)} · {item.paymentStatus || item.status || '—'} · {formatDateTime(item.paidTime || item.createdAt)}</div>}/>
            <UserDetailSection title="提现" items={recent.withdrawals} empty="暂无提现记录" renderItem={item => <div key={item.id} style={{ padding: '9px 0', borderTop: `1px solid ${C.hair}`, fontSize: 13 }}>{financeMoney(item.amount)} · {financeStatusLabel[item.status] || item.status || '—'} · {formatDateTime(item.createdAt)}</div>}/>
            <UserDetailSection title="管理员操作" items={recent.adminActions} empty="暂无管理员操作记录" renderItem={item => <div key={item.id} style={{ padding: '9px 0', borderTop: `1px solid ${C.hair}`, fontSize: 13 }}>{item.actionType || item.type || item.action} · {item.reason || '无原因'} · {formatDateTime(item.createdAt)}</div>}/>
          </div>
        </div>
      )}
      {!selected && results.length === 0 && !error && !loading && <div style={{ marginTop: 18, padding: 48, textAlign: 'center', color: C.ink3, border: `1px dashed ${C.hair2}`, borderRadius: 14 }}>输入搜索条件定位用户</div>}
    </div>
  );
}

const userActionDefinitions = [
  { key: 'grant', title: '赠送会员月份', description: '为用户追加 1–36 个月会员时间。', method: 'POST', path: id => `/global/users/${id}/subscription/extend`, valueLabel: '赠送月数', valuePlaceholder: '例如 3', inputType: 'number' },
  { key: 'expiry', title: '修改到期时间 / 等级', description: '按北京时间设置到期日，并可同时调整会员等级。', method: 'PATCH', path: id => `/global/users/${id}/subscription`, valueLabel: '新的到期时间（北京时间）', inputType: 'datetime-local', hasLevel: true, dangerous: true },
  { key: 'ban', title: '封禁账户', description: '阻止用户继续登录和操作。', method: 'POST', path: id => `/global/users/${id}/ban`, dangerous: true },
  { key: 'unban', title: '解除封禁', description: '恢复账户访问能力。', method: 'POST', path: id => `/global/users/${id}/unban`, dangerous: true },
];

export const beijingLocalToIso = value => {
  if (!value) return '';
  const withTimezone = value.length === 16 ? `${value}:00+08:00` : `${value}+08:00`;
  const date = new Date(withTimezone);
  return Number.isNaN(date.getTime()) ? '' : date.toISOString();
};

export function UserOperationsPage({ token, initialUserId = '' }) {
  const [userId, setUserId] = useState(initialUserId);
  const [action, setAction] = useState(null);
  const [value, setValue] = useState('');
  const [level, setLevel] = useState('');
  const [reason, setReason] = useState('');
  const [confirmed, setConfirmed] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [result, setResult] = useState('');

  const close = () => { setAction(null); setValue(''); setLevel(''); setReason(''); setConfirmed(false); setResult(''); };
  const submit = async () => {
    setSubmitting(true);
    setResult('');
    try {
      const body = { reason: reason.trim() };
      if (action.key === 'grant') body.months = Number(value);
      if (action.key === 'expiry') {
        body.endDate = beijingLocalToIso(value);
        if (level !== '') body.level = Number(level);
      }
      const response = await fetch(adminApiUrl(action.path(userId.trim())), {
        method: action.method,
        headers: { ...adminAuthHeaders(token), 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      });
      const payload = await response.json().catch(() => ({}));
      if (!response.ok) throw new Error(payload.error || `HTTP ${response.status}`);
      setResult('操作已由服务端确认完成。');
    } catch (requestError) {
      setResult(`操作未执行：${requestError.message || '接口请求失败'}`);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div>
      <div style={{ marginBottom: 28 }}>
        <SectionTitle label="Account operations" title="用户操作"/>
        <p style={{ margin: 0, color: C.ink3, fontSize: 13 }}>所有变更都由服务端落审计日志；只有服务端确认成功后，页面才显示完成。</p>
      </div>
      <div className="admin-panel-shell"><div className="admin-panel-core">
        <label style={{ display: 'block', marginBottom: 7, color: C.ink3, fontSize: 11 }}>目标用户 ID</label>
        <Input testId="admin-action-user-id" value={userId} onChange={setUserId} placeholder="输入完整用户 ID"/>
      </div></div>
      <div className="admin-user-detail-grid" style={{ display: 'grid', gridTemplateColumns: 'repeat(2, minmax(0, 1fr))', gap: 12 }}>
        {userActionDefinitions.map(definition => (
          <div key={definition.key} style={{ padding: 20, background: '#fff', border: `1px solid ${definition.dangerous ? '#efc9c2' : C.hair}`, borderRadius: 14 }}>
            <div style={{ fontFamily: C.serif, fontSize: 23 }}>{definition.title}</div>
            <p style={{ color: C.ink3, fontSize: 13, lineHeight: 1.6 }}>{definition.description}</p>
            <Btn testId={`admin-action-open-${definition.key}`} variant={definition.dangerous ? 'outline' : 'primary'} disabled={!userId.trim()} onClick={() => setAction(definition)}>进入确认</Btn>
          </div>
        ))}
      </div>
      {action && (
        <div role="dialog" aria-modal="true" aria-label={action.title} style={{ position: 'fixed', inset: 0, zIndex: 40, background: 'rgba(26,22,20,.68)', display: 'grid', placeItems: 'center', padding: 18 }}>
          <div style={{ width: 'min(100%, 460px)', padding: 24, borderRadius: 18, background: C.paper }}>
            <SectionTitle label={action.dangerous ? '危险操作 · 二次确认' : '操作确认'} title={action.title}/>
            <div style={{ marginBottom: 14, padding: 12, borderRadius: 10, background: '#fff', fontFamily: C.mono, fontSize: 11, overflowWrap: 'anywhere' }}>USER · {userId}</div>
            {action.valueLabel && <div style={{ marginBottom: 13 }}><label style={{ display: 'block', marginBottom: 6, fontSize: 11, color: C.ink3 }}>{action.valueLabel}</label><Input type={action.inputType} value={value} onChange={setValue} placeholder={action.valuePlaceholder}/></div>}
            {action.hasLevel && <div style={{ marginBottom: 13 }}><label style={{ display: 'block', marginBottom: 6, fontSize: 11, color: C.ink3 }}>会员等级（可选）</label><select data-testid="admin-action-level" value={level} onChange={event => setLevel(event.target.value)} style={{ width: '100%', minHeight: 44, padding: '0 13px', border: `1.5px solid ${C.hair}`, borderRadius: 10, background: '#fff', color: C.ink2 }}><option value="">仅修改日期</option><option value="0">等级 0</option><option value="1">等级 1</option><option value="2">等级 2</option></select></div>}
            <label style={{ display: 'block', marginBottom: 6, fontSize: 11, color: C.ink3 }}>操作原因（必填）</label>
            <TextArea testId="admin-action-reason" value={reason} onChange={setReason} placeholder="说明为什么需要执行此操作"/>
            {action.dangerous && <label style={{ display: 'flex', gap: 9, alignItems: 'flex-start', marginTop: 12, fontSize: 13, lineHeight: 1.5 }}><input data-testid="admin-action-confirm" type="checkbox" checked={confirmed} onChange={event => setConfirmed(event.target.checked)}/><span>我已核对用户 ID，并理解此操作会立即影响用户账户。</span></label>}
            {result && <div data-testid="admin-action-result" role="status" style={{ marginTop: 12, color: result.startsWith('操作已') ? '#267A46' : '#A53636', fontSize: 13 }}>{result}</div>}
            <div className="admin-button-row" style={{ marginTop: 18, justifyContent: 'flex-end' }}>
              <Btn variant="outline" onClick={close}>取消</Btn>
              <Btn testId="admin-action-submit" variant="brand" onClick={submit} disabled={submitting || reason.trim().length < 2 || reason.trim().length > 500 || (action.dangerous && !confirmed) || (action.valueLabel && !value.trim()) || (action.key === 'grant' && (!Number.isInteger(Number(value)) || Number(value) < 1 || Number(value) > 36)) || (action.key === 'expiry' && !beijingLocalToIso(value))}>{submitting ? '提交中…' : '确认提交'}</Btn>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function TodosPage({ token, active, period, pending, onNavigate, onDashboardChanged }) {
  const current = ['invoices', 'messages', 'operations'].includes(active) ? active : 'invoices';
  return (
    <div>
      <div style={{ marginBottom: 28 }}>
        <SectionTitle label="To do" title="待办中心"/>
        <div className="admin-kpi-grid" style={{ display: 'grid', gridTemplateColumns: 'repeat(3, minmax(0, 1fr))', gap: 10, marginTop: 16 }}>
          <Kpi label="待审发票" value={pending.invoiceCount ?? 0} warn={(pending.invoiceCount ?? 0) > 0}/>
          <Kpi label="未读留言" value={pending.unreadReplyCount ?? 0} warn={(pending.unreadReplyCount ?? 0) > 0}/>
          <Kpi label="系统任务" value="今日" hint="查看运行与异常"/>
        </div>
      </div>
      <div className="admin-todo-tabs" style={{ display: 'flex', gap: 8, marginBottom: 22 }}>
        <Btn testId="admin-todo-invoices" variant={current === 'invoices' ? 'primary' : 'outline'} onClick={() => onNavigate('invoices')}>发票</Btn>
        <Btn testId="admin-todo-messages" variant={current === 'messages' ? 'primary' : 'outline'} onClick={() => onNavigate('messages', { period: 'today' })}>留言</Btn>
        <Btn testId="admin-todo-operations" variant={current === 'operations' ? 'primary' : 'outline'} onClick={() => onNavigate('operations', { period: 'today' })}>系统任务</Btn>
      </div>
      {current === 'invoices' && (
        <InvoicesPage token={token} onDashboardChanged={onDashboardChanged}/>
      )}
      {current === 'messages' && (
        <MessagesPage token={token} period={period}/>
      )}
      {current === 'operations' && (
        <OperationsPage token={token}/>
      )}
    </div>
  );
}

// ─── Main ─────────────────────────────────────────────────────────────────────

const isDashboardPayload = payload => Boolean(
  payload
  && payload.summary && typeof payload.summary === 'object'
  && payload.income && typeof payload.income === 'object'
  && payload.finance && typeof payload.finance === 'object'
  && payload.pending && typeof payload.pending === 'object'
  && payload.expired && typeof payload.expired === 'object'
);

export const Admin = () => {
  const initialLocation = getAdminLocation();
  const initialUserId = new URLSearchParams(window.location.search).get('userId') || '';
  const storedToken = localStorage.getItem('admintoken');
  const savedToken = normalizeAdminToken(storedToken);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [dashboardError, setDashboardError] = useState('');
  const [page, setPage] = useState(initialLocation.view);
  const [filters, setFilters] = useState({ period: initialLocation.period, type: initialLocation.type });
  const [actionUserId, setActionUserId] = useState(initialUserId);

  const navigatePage = (view, params = {}) => {
    const next = new URLSearchParams();
    next.set('view', view);
    if (params.period) next.set('period', params.period);
    if (params.type) next.set('type', params.type);
    if (params.userId) next.set('userId', params.userId);
    window.history.replaceState({}, '', `${window.location.pathname}?${next.toString()}`);
    setFilters({ period: params.period || 'month', type: params.type || 'all' });
    if (params.userId) setActionUserId(params.userId);
    setPage(view);
  };

  const fetchData = async () => {
    setLoading(true);
    setDashboardError('');
    try {
      const response = await fetch(adminApiUrl('/global/dashboard'), {
        headers: adminAuthHeaders(savedToken),
      });
      const payload = await response.json().catch(() => ({}));
      if (!response.ok) throw new Error(payload.error || `HTTP ${response.status}`);
      if (!isDashboardPayload(payload)) throw new Error('管理员概览响应不完整');
      setData(payload);
    } catch (requestError) {
      setData(null);
      setDashboardError(requestError.message || '管理员概览加载失败');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (savedToken && storedToken !== savedToken) {
      localStorage.setItem('admintoken', savedToken);
    }
    if (savedToken) fetchData();
  }, []);

  if (!savedToken) return <TokenPanel />;

  const s = data?.summary || {};
  const today = data?.today || {};
  const inc = data?.income || {};
  const fin = data?.finance || {};
  const pend = data?.pending || {};
  const exp = data?.expired || {};
  const sess = data?.sessions || {};
  const rt = data?.runtime || {};

  return (
    <div className="admin-shell" style={{ height: '100vh', overflowY: 'auto', background: C.paper }}>
      <AdminResponsiveStyles />

      {/* HEADER */}
      <div className="admin-header" style={{
        background: C.ink, padding: '16px 48px',
        display: 'flex', alignItems: 'center', justifyContent: 'space-between',
        position: 'sticky', top: 10, zIndex: 10, width: 'calc(100% - 24px)', maxWidth: 1180,
        margin: '10px auto 0', borderRadius: 20, boxSizing: 'border-box',
      }}>
        <div className="admin-header-main" style={{ display: 'flex', alignItems: 'center', gap: 24 }}>
          <div className="admin-brand" style={{ display: 'flex', alignItems: 'baseline', gap: 12 }}>
            <span style={{ fontFamily: C.serif, fontSize: 22, color: '#fff', fontWeight: 400 }}>PICSECT</span>
            <span style={{ fontFamily: C.mono, fontSize: 10, letterSpacing: 1.4, textTransform: 'uppercase', color: C.brand }}>Admin</span>
          </div>
          <div className="admin-nav-scroll" style={{ display: 'flex', gap: 6 }}>
            <Btn small variant="ghost" active={page === 'today'} onClick={() => navigatePage('today', { period: 'today' })}>今日</Btn>
            <Btn testId="admin-nav-users" small variant="ghost" active={page === 'users'} onClick={() => navigatePage('users')}>用户</Btn>
            <Btn testId="admin-nav-user-actions" small variant="ghost" active={page === 'user-actions'} onClick={() => navigatePage('user-actions')}>用户操作</Btn>
            <Btn small variant="ghost" active={page === 'finance'} onClick={() => navigatePage('finance')}>财务</Btn>
            <Btn testId="admin-nav-todos" small variant="ghost" active={['todos', 'invoices', 'messages', 'operations'].includes(page)} onClick={() => navigatePage('todos')}>待办</Btn>
          </div>
        </div>
        <div className="admin-header-actions" style={{ display: 'flex', gap: 10, alignItems: 'center' }}>
          {loading && <span style={{ fontFamily: C.mono, fontSize: 11, color: 'rgba(255,255,255,.4)' }}>loading…</span>}
          <Btn small variant="ghost" onClick={fetchData}>刷新</Btn>
          <Btn small variant="ghost" onClick={() => { localStorage.removeItem('admintoken'); window.location.reload(); }}>退出</Btn>
        </div>
      </div>

      <div className="admin-content" style={{ maxWidth: 1100, margin: '0 auto', padding: '48px 40px' }}>

        {page === 'today' && data && (
          <TodayOverview today={today} summary={s} income={inc} finance={fin} pending={pend} onNavigate={navigatePage}/>
        )}

        {page === 'users' && (
          <UsersPage token={savedToken} onOpenActions={userId => navigatePage('user-actions', { userId })}/>
        )}

        {page === 'user-actions' && (
          <UserOperationsPage token={savedToken} initialUserId={actionUserId}/>
        )}

        {/* ── OVERVIEW (shown under 今日, below TodayOverview) ── */}
        {page === 'today' && data && (
          <>
            {/* Summary */}
            <div className="admin-section" style={{ marginBottom: 36 }}>
              <SectionTitle label="Overview" title="平台概览"/>
              <div className="admin-kpi-grid" style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 14 }}>
                <Kpi label="用户总数" value={s.users ?? 0} hint={`今日新增 ${s.todayNewUserCount ?? 0} · 活跃 ${s.todayActiveUserCount ?? 0}`}/>
                <Kpi label="订单总数" value={s.orders ?? 0}/>
                <Kpi label="相册总数" value={s.albums ?? 0}/>
                <Kpi label="原片总量" value={Number(s.pictureCount || 0).toLocaleString()}/>
              </div>
            </div>

            {/* Income */}
            <div className="admin-section" style={{ marginBottom: 36 }}>
              <SectionTitle label="Income" title="收入"/>
              <div className="admin-kpi-grid" style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 14 }}>
                <Kpi label="总收入" value={`¥${formatMoney(inc.total)}`} large accent/>
                <Kpi label="本月收入" value={`¥${formatMoney(inc.incomOfThisMonth)}`}/>
                <Kpi label="今日收入" value={`¥${formatMoney(inc.incomOfToday)}`}/>
              </div>
            </div>

            {/* Finance summary */}
            <div className="admin-section" style={{ marginBottom: 36 }}>
              <SectionTitle label="Finance" title="财务摘要"/>
              <div className="admin-kpi-grid" style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 14 }}>
                <Kpi label="本月加片笔数" value={fin.paidSelectionCountOfThisMonth ?? 0}/>
                <Kpi label="本月加片收入" value={`¥${formatMoney(fin.paidSelectionIncomeOfThisMonth)}`} accent/>
                <Kpi label="待处理提现" value={fin.pendingWithdrawCount ?? 0} warn={(fin.pendingWithdrawCount ?? 0) > 0} hint={(fin.pendingWithdrawCount ?? 0) > 0 ? `¥${formatMoney(fin.pendingWithdrawAmount)}` : undefined}/>
                <Kpi label="本月留言" value={s.messagesOfThisMonthCount ?? 0}/>
              </div>
            </div>

            {/* Session monitor */}
            <div className="admin-section" style={{ marginBottom: 36 }}>
              <SectionTitle label="Sessions" title="登录态监控"/>
              <div style={{ fontSize: 12, color: C.ink3, fontFamily: C.mono, marginBottom: 14 }}>
                当前有效 session ≠ 用户正在实时操作；今日活跃用户统计另见平台概览。
              </div>

              {/* KPI row */}
              <div className="admin-kpi-grid" style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 14, marginBottom: 16 }}>
                <Kpi label="当前有效 Session 数" value={sess?.activeSessionCount ?? 0}/>
                <Kpi label="多 Session 用户数" value={sess?.multiSessionUserCount ?? 0} warn={(sess?.multiSessionUserCount ?? 0) > 0}/>
                <Kpi label="单用户最大 Session 数" value={sess?.maxSessionCountPerUser ?? 0} warn={(sess?.maxSessionCountPerUser ?? 0) > 3}/>
              </div>

              {/* Max session user highlight */}
              {sess?.maxSessionUser && (
                <div className="admin-session-highlight" style={{
                  background: '#fff8f8', border: '1px solid #f5c4c4',
                  borderRadius: 12, padding: '14px 20px', marginBottom: 16,
                  display: 'flex', alignItems: 'center', gap: 32,
                }}>
                  <div style={{ fontFamily: C.mono, fontSize: 10, letterSpacing: 1.2, textTransform: 'uppercase', color: '#A53636', fontWeight: 600 }}>
                    Session 最多用户
                  </div>
                  <div style={{ fontFamily: C.mono, fontSize: 13, color: C.ink2 }}>
                    {sess.maxSessionUser.user?.username ?? '—'}
                  </div>
                  <div style={{ fontFamily: C.mono, fontSize: 13, color: C.ink3 }}>
                    {sess.maxSessionUser.user?.phone ?? '—'}
                  </div>
                  <div className="admin-session-highlight-count" style={{
                    marginLeft: 'auto',
                    background: '#A53636', color: '#fff',
                    borderRadius: 6, padding: '3px 10px',
                    fontFamily: C.mono, fontSize: 13, fontWeight: 600,
                  }}>
                    {sess.maxSessionUser.sessionCount} sessions
                  </div>
                </div>
              )}

              {/* Users table */}
              <div style={{ background: '#fff', border: `1px solid ${C.hair}`, borderRadius: 12, overflow: 'hidden' }}>
                {/* Table header */}
                <div className="admin-session-table-head" style={{
                  display: 'grid', gridTemplateColumns: '1fr 1fr 120px 160px',
                  padding: '10px 20px', borderBottom: `1px solid ${C.hair}`,
                  fontFamily: C.mono, fontSize: 10, letterSpacing: 1.2,
                  textTransform: 'uppercase', color: C.ink3,
                }}>
                  <span>用户名</span>
                  <span>手机号</span>
                  <span>Session 数</span>
                  <span style={{ opacity: 0.6 }}>用户 ID</span>
                </div>

                {/* Rows */}
                {(!sess?.users?.length) ? (
                  <div style={{ padding: '32px 20px', textAlign: 'center', fontFamily: C.mono, fontSize: 12, color: C.ink3 }}>
                    暂无有效 session
                  </div>
                ) : (
                  <div style={{ maxHeight: 360, overflowY: 'auto' }}>
                    {sess.users.map((item, i) => {
                      const multi = item.sessionCount > 1;
                      return (
                        <div className="admin-session-row" key={i} style={{
                          display: 'grid', gridTemplateColumns: '1fr 1fr 120px 160px',
                          padding: '10px 20px',
                          borderBottom: `1px solid ${C.hair}`,
                          background: multi ? '#fff8f8' : '#fff',
                          alignItems: 'center',
                        }}>
                          <span style={{ fontFamily: C.mono, fontSize: 13, color: C.ink2 }}>
                            {item.user?.username ?? '—'}
                          </span>
                          <span style={{ fontFamily: C.mono, fontSize: 13, color: C.ink3 }}>
                            {item.user?.phone ?? '—'}
                          </span>
                          <span style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                            <span style={{
                              fontFamily: C.mono, fontSize: 13, fontWeight: 600,
                              color: multi ? '#A53636' : C.ink,
                            }}>
                              {item.sessionCount}
                            </span>
                            {multi && (
                              <span style={{
                                fontSize: 10, fontWeight: 700, letterSpacing: 0.5,
                                padding: '2px 6px', borderRadius: 4,
                                background: '#fde8e8', color: '#A53636',
                              }}>
                                多端
                              </span>
                            )}
                          </span>
                          <span style={{ fontFamily: C.mono, fontSize: 11, color: C.ink3, opacity: 0.7 }}>
                            {item.user?.id ?? '—'}
                          </span>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            </div>

            {/* Pending & Expired */}
            <div className="admin-section" style={{ marginBottom: 36 }}>
              <SectionTitle label="Alerts" title="待处理事项"/>
              <div className="admin-kpi-grid" style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 14, marginBottom: 16 }}>
                <Kpi label="待审发票" value={pend.invoiceCount ?? 0} warn={(pend.invoiceCount ?? 0) > 0}/>
                <Kpi label="处理中发票" value={pend.processingInvoiceCount ?? 0}/>
                <Kpi label="未读回复" value={pend.unreadReplyCount ?? 0} warn={(pend.unreadReplyCount ?? 0) > 0}/>
                <Kpi label="待处理提现" value={pend.withdrawCount ?? 0} warn={(pend.withdrawCount ?? 0) > 0}/>
              </div>

              {/* Expired */}
              <div className="admin-expired-card" style={{
                background: (exp.orders ?? 0) > 0 ? '#fff8f8' : '#fff',
                border: `1px solid ${(exp.orders ?? 0) > 0 ? '#f5c4c4' : C.hair}`,
                borderRadius: 12, padding: '16px 20px',
                display: 'flex', alignItems: 'center', gap: 32,
              }}>
                <div>
                  <div style={{ fontFamily: C.mono, fontSize: 10, letterSpacing: 1.2, textTransform: 'uppercase', color: (exp.orders ?? 0) > 0 ? '#A53636' : C.ink3, fontWeight: 600, marginBottom: 4 }}>Expired</div>
                  <div style={{ display: 'flex', gap: 32 }}>
                    <div style={{ fontFamily: C.mono, fontSize: 13, color: C.ink2 }}>过期订单 <strong style={{ color: (exp.orders ?? 0) > 0 ? '#A53636' : C.ink }}>{exp.orders ?? 0}</strong></div>
                    <div style={{ fontFamily: C.mono, fontSize: 13, color: C.ink2 }}>过期分享 <strong>{exp.shares ?? 0}</strong></div>
                  </div>
                </div>
              </div>
            </div>

            {/* API health (runtime) */}
            {rt && (rt.sampleCount ?? 0) >= 0 && (
              <div className="admin-section" style={{ marginBottom: 36 }}>
                <SectionTitle label="API health" title="接口健康"/>
                <div style={{ fontSize: 12, color: C.ink3, fontFamily: C.mono, marginBottom: 14 }}>
                  最近 {rt.windowSeconds ? Math.round(rt.windowSeconds / 60) : '—'} 分钟窗口 · 样本 {rt.sampleCount ?? 0} 次请求 · RPS {rt.rps ?? 0}
                </div>
                <div className="admin-kpi-grid" style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 14, marginBottom: 16 }}>
                  <Kpi label="延迟 P50" value={`${rt.p50Ms ?? 0} ms`}/>
                  <Kpi label="延迟 P95" value={`${rt.p95Ms ?? 0} ms`} warn={(rt.p95Ms ?? 0) > 1000}/>
                  <Kpi label="延迟 P99" value={`${rt.p99Ms ?? 0} ms`} warn={(rt.p99Ms ?? 0) > 2000}/>
                  <Kpi label="错误率" value={`${(((rt.errorRate ?? 0) * 100).toFixed(2))}%`} warn={(rt.errorRate ?? 0) > 0.01} hint={`${rt.errorCount ?? 0} 次 5xx`}/>
                </div>
                <div className="admin-kpi-grid" style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: 14, marginBottom: 16 }}>
                  <Kpi label="进行中请求" value={rt.inFlight ?? 0}/>
                  <Kpi label="进程内存 RSS" value={`${rt.memoryRssMb ?? 0} MB`} warn={(rt.memoryRssMb ?? 0) > 1200}/>
                </div>

                {/* Slowest routes */}
                <div style={{ background: '#fff', border: `1px solid ${C.hair}`, borderRadius: 12, overflow: 'hidden' }}>
                  <div style={{
                    display: 'grid', gridTemplateColumns: '1fr 100px 110px 110px',
                    padding: '10px 20px', borderBottom: `1px solid ${C.hair}`,
                    fontFamily: C.mono, fontSize: 10, letterSpacing: 1.2,
                    textTransform: 'uppercase', color: C.ink3,
                  }}>
                    <span>最慢路由</span>
                    <span>次数</span>
                    <span>平均</span>
                    <span>最大</span>
                  </div>
                  {(!rt.slowRoutes?.length) ? (
                    <div style={{ padding: '32px 20px', textAlign: 'center', fontFamily: C.mono, fontSize: 12, color: C.ink3 }}>
                      暂无请求样本
                    </div>
                  ) : (
                    rt.slowRoutes.map((route, i) => (
                      <div key={i} style={{
                        display: 'grid', gridTemplateColumns: '1fr 100px 110px 110px',
                        padding: '10px 20px', borderBottom: `1px solid ${C.hair}`, alignItems: 'center',
                        background: (route.max ?? 0) > 1000 ? '#fff8f8' : '#fff',
                      }}>
                        <span style={{ fontFamily: C.mono, fontSize: 12, color: C.ink2, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{route.route}</span>
                        <span style={{ fontFamily: C.mono, fontSize: 12, color: C.ink3 }}>{route.count}</span>
                        <span style={{ fontFamily: C.mono, fontSize: 12, color: C.ink2 }}>{route.averageMs} ms</span>
                        <span style={{ fontFamily: C.mono, fontSize: 12, fontWeight: 600, color: (route.max ?? 0) > 1000 ? '#A53636' : C.ink }}>{route.max} ms</span>
                      </div>
                    ))
                  )}
                </div>
              </div>
            )}
          </>
        )}

        {page === 'today' && !data && !loading && (
          <div data-testid="admin-dashboard-error" role="alert" style={{ padding: 24, background: '#fff8f8', border: '1px solid #f5c4c4', borderRadius: 12, color: '#A53636', fontSize: 13, lineHeight: 1.7 }}>
            管理员概览加载失败：{dashboardError || '请刷新重试'}
          </div>
        )}

        {/* ── FINANCE ── */}
        {page === 'finance' && (
          <>
            <div style={{ marginBottom: 28 }}>
              <SectionTitle label={`Finance · ${filters.period === 'today' ? '今日' : '本月'}`} title={filters.type === 'withdrawal' ? '提现用户明细' : filters.type === 'payment' ? '支付用户明细' : '本月财务明细'}/>
            </div>
            <FinancePage token={savedToken} period={filters.period} type={filters.type} onClearFilter={() => navigatePage('finance')}/>
          </>
        )}

        {['todos', 'invoices', 'messages', 'operations'].includes(page) && (
          <TodosPage token={savedToken} active={page} period={filters.period} pending={pend} onNavigate={navigatePage} onDashboardChanged={fetchData}/>
        )}
      </div>
    </div>
  );
};

export default Admin;
